import { ReactElement, useState, useEffect, SelectHTMLAttributes, useRef } from 'react';
import { useTranslation } from 'react-i18next';

// optionの型定義
interface OptionType {
  value: string;
  label: string;
  colorHex?: string;
  isDivider?: boolean;
  isLocked?: boolean;
}

// Selectコンポーネントのプロパティ型定義
interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'defaultValue'> {
  options: OptionType[];
  label?: string;
  defaultValue?: OptionType; // valueだけでなくlabelも扱う
  onValueChange?: (value: OptionType) => void;
  className?: string;
  dropdownClassName?: string; // ドロップダウンメニューの幅/見た目を個別指定
  dropdownStyle?: React.CSSProperties; // ドロップダウンメニューのスタイル
  noBorder?: boolean; // 外枠を削除するオプション
  noWrap?: boolean; // テキストを折り返さない（white-space: nowrap）
}

const Select: React.FC<SelectProps> = ({
  options,
  label,
  defaultValue,
  onValueChange,
  className = '',
  dropdownClassName = '',
  dropdownStyle,
  noBorder = false,
  noWrap = false,
  ...props
}): ReactElement => {
  // 選択状態のオプションを保持
  const [selectedValue, setSelectedValue] = useState<OptionType>(
    defaultValue || { value: '', label: '' },
  );
  // ドロップダウンの表示状態
  const [isOpen, setIsOpen] = useState(false);
  // 参照
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common');

  // 外部クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // defaultValueが変わったらstateも更新
  useEffect(() => {
    if (defaultValue) {
      setSelectedValue(defaultValue);
    }
  }, [defaultValue]);

  // オプション選択時の処理
  const handleSelectOption = (option: OptionType) => {
    if (option.isLocked) {
      setIsOpen(false);
      onValueChange?.(option);
      return;
    }
    setSelectedValue(option);
    setIsOpen(false);
    onValueChange?.(option);
  };

  // ネイティブセレクト要素の変更ハンドラー（アクセシビリティ対応）
  const handleNativeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    const selectedOption = options.find((opt) => opt.value === value);
    if (selectedOption) {
      setSelectedValue(selectedOption);
      onValueChange?.(selectedOption);
    }
  };

  // レンダリング
  return (
    <div className="select-container relative" ref={dropdownRef}>
      {/* ラベル表示 */}
      {label && <label className="select-label">{label}</label>}

      {/* カスタムセレクト */}
      <div className={`relative min-w-full ${className}`}>
        {/* 選択表示部分 */}
        <div
          className={`flex items-center justify-between ${noBorder ? '' : 'border-2 border-lightest-gray rounded-lg'} ${noBorder ? 'py-0 px-0' : 'py-2 px-3'} cursor-pointer ${noBorder ? 'bg-transparent' : 'bg-white'}`}
          style={{ fontSize: '0.875rem' }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span className="flex items-center gap-2" style={{ fontSize: '0.875rem', ...(noWrap && { whiteSpace: 'nowrap' }) }}>
            {selectedValue?.colorHex && (
              <span
                className="inline-block w-3 h-3 rounded-sm border border-gray-300"
                style={{ backgroundColor: selectedValue.colorHex }}
              />
            )}
            {selectedValue.label || t('select.placeholder')}
            {selectedValue?.isLocked && <span className="text-gray-400 text-xs">🔒</span>}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>
        </div>

        {/* ドロップダウンメニュー */}
        {isOpen && (
          <div 
            className={`absolute z-20 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto ${dropdownClassName}`}
            style={dropdownStyle}
          >
            {!options || options.length === 0 ? (
              <div className="px-4 py-2 text-gray-500">{t('select.noOptions')}</div>
            ) : (
              options.map((option, index) => (
                option.isDivider ? (
                  <div key={`divider-${index}`} className="my-1 border-t border-gray-200" />
                ) : (
                  <div
                    key={option.value || index}
                    className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    style={{ fontSize: '0.875rem' }}
                    onClick={() => handleSelectOption(option)}
                  >
                    <span className="flex items-center gap-2" style={{ fontSize: '0.875rem', ...(noWrap && { whiteSpace: 'nowrap' }) }}>
                      {option?.colorHex && (
                        <span
                          className="inline-block w-3 h-3 rounded-sm border border-gray-300"
                          style={{ backgroundColor: option.colorHex }}
                        />
                      )}
                      {option.label || t('select.fallbackOption', { index: index + 1 })}
                      {option.isLocked && <span className="text-gray-400 text-xs">🔒</span>}
                    </span>
                  </div>
                )
              ))
            )}
          </div>
        )}

        {/* 非表示のネイティブセレクト（アクセシビリティとフォームサブミット用） */}
        <select
          className="sr-only"
          value={selectedValue.value}
          onChange={handleNativeChange}
          aria-hidden="true"
          tabIndex={-1} // フォーカス対象から外す
          {...props}
        >
          {(!options || options.length === 0) && <option value="">{t('select.noOptions')}</option>}
          {options.filter(o => !o.isDivider).map((option, index) => (
            <option key={option.value || index} value={option.value}>
              {option.label || t('select.fallbackOption', { index: index + 1 })}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default Select;
